import clsx from 'clsx';
import { DateTime } from 'luxon';
import React, {
  ReactElement, useCallback,useEffect, useId, useRef, useState
} from 'react';
import { FiEdit2, FiRotateCcw } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { toast,ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import AgentVariables from '@/components/agentVariables/agentVariables';
import { ChatConversationMessage } from '@/components/chatConversation/chatConversation';
import { Loader } from '@/components/loader/loader';
import PlaygroundChat from '@/components/playgroundChat/playgroundChat';
import { PlaygroundType } from '@/components/playgroundHeader/playgroundHeader';
import { useAuthContext } from '@/hooks/useAuthContext';
import {
  AgentConfig,
  AgentConfigGetArgs,
  AgentInvokeApiRes,
  AgentInvokeLowCodeArgs,
  AgentTypeEnum
} from '@/types/agent';
import {
  AgentInvokeApiReqUser,
  ChatMessageContentTextual,
  ChatMsgHistory,
  ChatMsgHistoryItem,
  ChatMsgRes,
  ChatMsgTypeEnum,
  ChatRoleEnum,
  CompletionResponseTypeEnum
} from '@/types/chat';

import styles from './page.module.scss';

type Props = {
  an?: string;
  i?: string;
  v?: string;
};

export default function AgentLowCode( props: Props ) {
  const navigate = useNavigate();
  const id = useId();

  const { sessionContext } = useAuthContext();

  const {
    an: propAgentName,
    i: propId,
    v: propVersion
  } = props;

  const [ sAgentName, setSAgentName ] = useState<string>( propAgentName || '' );
  const [ sChatMsgHistory, setSChatMsgHistory ] = useState<ChatMsgHistory>( [] );
  const [ sChatFMsgHistory, setSFChatMsgHistory ] = useState<Array<ChatConversationMessage>>( [] );
  const [ sChatUsers, setSChatUsers ] = useState<AgentInvokeApiReqUser[]>();
  const [ sConfigVersions, setSConfigVersions ] = useState<string[]>( [] );
  const [ sContext, setSContext ] = useState<string | undefined>();
  const [ sCorrelationId, setSCorrelationId ] = useState<string | undefined>();
  const [ sDebug, setSDebug ] = useState<boolean>( false );
  const [ sInput, setSInput ] = useState<string>( '' );
  const [ sInputs, setSInputs ] = useState<string | undefined>();
  const [ sIsLoading, setSIsLoading ] = useState<boolean>( true );
  const [ sIsWaiting, setSIsWaiting ] = useState<boolean>( false );

  const messagesEndRef = useRef<null | HTMLDivElement>( null );

  useEffect( () => {
    const fetchAgentVersionConfigData = async(): Promise<AgentConfig> => {
      const getAgentConfigArgs: AgentConfigGetArgs = {
        agentId: propId!,
        version: Number( propVersion )
      };
      const config: AgentConfig = await APIGateway.agents.agentConfigGet( getAgentConfigArgs );

      return config;
    };

    fetchAgentVersionConfigData()
      .then( ( config: AgentConfig ) => {
        // Only update agent name if not provided via props
        if ( !propAgentName ) {
          setSAgentName( config.name );
        }
        setSConfigVersions( config.versions );

        // Initialize inputs from START node schema
        // Filter out 'conversation' as it's always built from messages
        const startNode = config.steps.find( step => step.id === 'start' );
        if ( startNode && startNode.inputs ) {
          const inputVars = Object.keys( startNode.inputs )
            .filter( key => key !== 'conversation' )
            .map( key => ( {
              key,
              value: '',
              keyInfo: startNode.inputs![key].type
            } ) );
          setSInputs( JSON.stringify( inputVars ) );
        }

        // Initialize contexts from global context schema
        if ( config.context ) {
          const contextVars = Object.keys( config.context ).map( key => ( {
            key,
            value: '',
            keyInfo: config.context![key].type
          } ) );
          setSContext( JSON.stringify( contextVars ) );
        }

        setSIsLoading( false );
      } )
      .catch( ( e: Error ) => {
        console.error( e );
      } );
  }, [ props ] );

  useEffect( () => {
    const chatUsers = [
      {
        displayName: 'User',
        id: sessionContext!.userId!,
        type: ChatRoleEnum.USER
      },
      {
        displayName: sAgentName,
        id: sAgentName,
        type: ChatRoleEnum.ASSISTANT
      }
    ];

    setSChatUsers( chatUsers );
  }, [ sAgentName, sessionContext ] );

  const _invokeCall = async( messages: ChatMsgHistory ): Promise<AgentInvokeApiRes> => {
    // Convert array [{key, value, keyInfo?}] to object {key: value}
    const arrayToObject = ( items: Array<{ key: string; value: string; keyInfo?: string }> ) => {
      return items.reduce( ( acc, { key, value } ) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, string> );
    };

    const args: AgentInvokeLowCodeArgs = {
      agentId: propId!,
      clientId: 'aidp-studio',
      context: {
        values: []
      },
      contexts: sContext ? arrayToObject( JSON.parse( sContext ) ) : undefined,
      debug: sDebug,
      inputs: sInputs ? arrayToObject( JSON.parse( sInputs ) ) : undefined,
      messages,
      users: sChatUsers!,
      version: propVersion!
    };

    if( sCorrelationId ) {
      args.correlationId = sCorrelationId;
    }

    const res: AgentInvokeApiRes = await APIGateway.agents.agentInvokeLowCode( args );

    return res;
  };

  const onChangeVersion = ( e: React.SyntheticEvent<HTMLSelectElement> ) => {
    const version = e.currentTarget.value;
    navigate( `/agents/playground?i=${ propId }&t=${ AgentTypeEnum.LOW_CODE }&v=${ version }` );
  };

  const formatChatHistory = ( input: Array<ChatMsgHistoryItem> ) => {
    const formated = input.flatMap( ( msg: ChatMsgHistoryItem ): Array<ChatConversationMessage> => {
      if ( msg.content.type === ChatMsgTypeEnum.TEXTUAL_CONTENT ) {
        return [ { role: msg.role, text: msg.content.text as unknown as string } ];
      } else {
        const c = msg.content as any;
        if ( c.data.debug ) {
          return [ { role: msg.role, json: JSON.parse( c.data.debug ) as unknown as string },{ role: msg.role, text: c.data.resp as unknown as string } ];
        } else {
          return [ { role: msg.role, text: c.data.resp as unknown as string } ];
        }
      }} );
    return formated;
  };

  const onClickChat = useCallback( async() => {
    setSIsWaiting( true );

    try {
      const compReqMsg: ChatMsgHistoryItem<ChatMessageContentTextual> = {
        authorId: sessionContext!.userId!,
        content: {
          type: ChatMsgTypeEnum.TEXTUAL_CONTENT,
          text: sInput
        },
        createdAt: DateTime.now(),
        messageId: id.toString(),
        role: ChatRoleEnum.USER,
        updatedAt: DateTime.now()
      };

      const newChatMsgHistory: ChatMsgHistory = [
        ...sChatMsgHistory,
        compReqMsg
      ];

      setSChatMsgHistory( newChatMsgHistory );
      setSFChatMsgHistory( formatChatHistory( newChatMsgHistory ) );

      setTimeout( ()=>{
        messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
      }, 250 );

      setSInput( '' );

      const res: AgentInvokeApiRes = await _invokeCall( newChatMsgHistory );

      if( !res.length ) {
        throw new Error( 'no responses returned' );
      }

      // store correlationId generated by middleware
      setSCorrelationId( res[0].correlationId ); // same for every message

      for( const compMsg of res ) {
        if( compMsg.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_ERROR ) {
          throw new Error( compMsg.error_message || compMsg.type );
        }

        if( compMsg.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_FAILURE ) {
          throw new Error( compMsg.failure_message || compMsg.type );
        }

        if( compMsg.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_RESULT ) {
          const compMsgMessages = compMsg.messages.map( ( msg: ChatMsgRes ) => {
            const mapped: ChatMsgHistoryItem = {
              authorId: sAgentName,
              content: msg.content,
              createdAt: DateTime.now(),
              messageId: msg.messageId,
              role: ChatRoleEnum.ASSISTANT,
              updatedAt: DateTime.now()
            };

            return mapped;
          } );

          newChatMsgHistory.push( ...compMsgMessages );
        }
      }

      setSChatMsgHistory( newChatMsgHistory );
      setSFChatMsgHistory( formatChatHistory( newChatMsgHistory ) );

      setTimeout( () => {
        messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
      }, 250 );
    } catch( e ) {
      console.error( ( e as Error ).message );
      toast.error( ( e as Error ).message );
    }

    setSIsWaiting( false );
  }, [ sChatMsgHistory, sInput, sDebug ] );

  const onClickReset = () => {
    setSChatMsgHistory( [] );
    setSFChatMsgHistory( [] );
  };

  const renderInfo = (): ReactElement => {
    return (
      <>
        <span className={ styles.playground_info_name }>
          <p>{ sAgentName }</p>
        </span>


        <span className={ styles.playground_info_actions }>
          <span className={ clsx( 'version', styles.playground_info_version, styles.action_button ) }>
            Version
            <select value={ propVersion } onChange={ onChangeVersion }>
              {
                sConfigVersions.map( ( el: string, index: number ) => (
                  <option value={ el } key={ `version-option-${ index }` }>{ el }</option>
                ) )
              }
            </select>
          </span>

          <span className={ styles.action_button }>
            <input type='checkbox' id='debug' checked={ sDebug } onChange={ () => setSDebug( !sDebug ) }/>
            <label htmlFor='debug'>debug</label>
          </span>

          <span className={ styles.action_button } onClick={ onClickReset } >
            <FiRotateCcw /> reset
          </span>
          <Link
            className={ styles.action_button }
            to={ `/agents/edit/?id=${ propId }&version=${ propVersion }` }
          >
            <><FiEdit2 />Edit</>
          </Link>
        </span>
      </>
    );
  };

  const render = (): ReactElement => {
    return (
      <div className={ clsx( 'layout__col', 'overflow_hide' ) }>
        <div className={ clsx( 'layout__row', styles.playground_type ) }>
          <PlaygroundType label="Low-Code Agent" />
        </div>
        <div className={ clsx( 'layout__row', styles.playground_info ) }>
          { renderInfo() }
        </div>
        <div className={ clsx( 'layout__row', 'overflow_hide', styles.playground_chat ) }>
          <div className={ clsx( 'layout__col', styles.chat_context ) } data-testid="playground-context-panel">
            <div data-testid="playground-inputs-section">
              <AgentVariables
                label="Inputs"
                value={ sInputs }
                onChange={ setSInputs }
                readonlyKeys={ true }
                showKeyInfo={ true }
              />
            </div>
            <div data-testid="playground-context-section">
              <AgentVariables
                label="Context"
                value={ sContext }
                onChange={ setSContext }
                readonlyKeys={ true }
                showKeyInfo={ true }
              />
            </div>
          </div>
          <div className={ clsx( 'layout__col', 'overflow_hide', styles.chat_convo ) }>
            <div className={ clsx( 'layout__col', 'overflow_hide', styles.chat_container ) }>
              <PlaygroundChat
                id={ id }
                input={ sInput }
                isWaiting={ sIsWaiting }
                messages={ sChatFMsgHistory }
                enableStructured= { false }
                messagesEndRef={ messagesEndRef }
                onInputChange={ setSInput }
                onClickChat={ onClickChat }
                onClickStop={ () => {} }
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={ clsx( 'page', 'no_overflow' ) }>
      <ToastContainer />
      {
        sIsLoading
          ? <Loader />
          : render()
      }
    </div>
  );
}
